import dmUtils from '../../dmFramework/dmJS/dmUtils.js';
import endpoints from '../js/endpoints';

const publicMenuModule = {
    namespaced: true,
    state: () => ({
        districtSlug: '',
        publicMenu: {},
        sites: [],
        siteID: null,
        mealTimes: [],
        mealTimeId: null,
        categories: [],
        categoriesById: {},
        allergens: [],
        allergensById: {},
        nutrition: [],
        nutritionByNutritionComponentId: {},
        announcementText: '',
        announcements: {},
        nutritionDetails: {},
        menuItemsById: {},
    }),
    mutations: {
        setDistrictSlug(state, districtSlug) {
            state.districtSlug = districtSlug;
        },
        setPublicMenu(state, publicMenu) {
            state.publicMenu = publicMenu;
        },
        setSites(state, sites) {
            state.sites = sites;
        },
        setSiteID(state, siteID) {
            state.siteID = siteID;
            dmUtils.localStorageSet(state.districtSlug + '-siteID', siteID);
        },
        setMealTimeId(state, mealTimeId) {
            state.mealTimeId = mealTimeId;
            dmUtils.localStorageSet(state.districtSlug + '-mealTimeID', mealTimeId);
        },
        setSiteIDAndMealTimeId(state, { siteID, mealTimeId }) {
            state.siteID = siteID;
            state.mealTimeId = mealTimeId;
            dmUtils.localStorageSet(state.districtSlug + '-siteID', siteID);
            dmUtils.localStorageSet(state.districtSlug + '-mealTimeID', mealTimeId);
        },
        setMealTimes(state, mealTimes) {
            state.mealTimes = mealTimes;
        },
        setCategories(state, categories) {
            state.categories = categories;
            state.categoriesById = categories.reduce((acc, category) => {
                acc[category.CategoryId] = category;
                return acc;
            }, {});
        },
        setAllergens(state, allergenColumns) {
            state.allergens = allergenColumns;
            state.allergensById = allergenColumns.reduce((acc, allergen) => {
                acc[allergen.AllergenId] = allergen;
                return acc;
            }, {});
        },
        setNutrition(state, nutrition) {
            state.nutrition = nutrition;
            state.nutritionByNutritionComponentId = nutrition.reduce((acc, item) => {
                acc[item.NutritionComponentId] = item;
                return acc;
            }, {});
        },
        setAnnouncementText(state, text) {
            state.announcementText = text;
        },
        clearAnnouncementText(state) {
            setTimeout(() => {
                state.announcementText = '';
            }, 500);
        },
        setAnnouncements(state, announcements) {
            state.announcements = announcements;
        },
        setNutritionDetails(state, nutrition) {
            state.nutritionDetails = nutrition;
            state.menuItemsById = nutrition.MenuItems.reduce((acc, item) => {
                acc[item.MenuItemId] = item;
                return acc;
            }, {});
        },
    },
    actions: {
        setDistrictSlug({ commit }, districtSlug) {
            commit('setDistrictSlug', districtSlug);
        },
        loadFromLocalStorage({ commit, state }) {
            const storedSiteID = dmUtils.localStorageGet(state.districtSlug + '-siteID');
            const storedMealTimeID = dmUtils.localStorageGet(state.districtSlug + '-mealTimeID');

            if (storedSiteID != null && storedMealTimeID !== '') {
                commit('setSiteIDAndMealTimeId', {
                    siteID: parseInt(storedSiteID, 10),
                    mealTimeId: parseInt(storedMealTimeID, 10),
                });
            }
        },
        async setMealTimeId({ commit, dispatch, state }, mealTimeId) {
            commit('setMealTimeId', mealTimeId);
            dmUtils.localStorageSet(state.districtSlug + '-mealTimeID', mealTimeId);
            await dispatch('initializeMealTime');
        },
        async setSiteID({ commit, dispatch, state }, siteID) {
            commit('setSiteID', siteID);
            await dispatch('initializeSite');
        },
        getStyleDetails({ commit, state }) {
            return endpoints.getStyleDetails({ districtSlug: state.districtSlug }).then((response) => {
                let style = document.createElement('style');
                style.type = 'text/css';
                style.innerHTML = '.app-primary-color { color: ' + response.PrimaryColor + ' !important; }';
                document.getElementsByTagName('head')[0].appendChild(style);

                if (!response.IsPublicMenuPublished) {
                    response.HeaderText = response.PreviewModeText;
                }
                commit('setPublicMenu', response);
            });
        },
        getSites({ commit, state }) {
            return endpoints.getSites({ districtSlug: state.districtSlug }).then((response) => {
                commit('setSites', response);
            });
        },
        getMealTimes({ commit, state }) {
            return endpoints.getMealTimes({ SiteID: state.siteID }).then((response) => {
                commit('setMealTimes', response);
            });
        },
        getMenuItemCategories({ commit, state }) {
            return endpoints.getMenuItemCategories({ SiteID: state.siteID }).then((response) => {
                response.forEach((c) => {
                    c.IconUrl += '?v=' + new Date().toISOString();
                });
                commit('setCategories', response);
            });
        },
        getNutrition({ commit, state }) {
            return endpoints.getNutrition({ districtSlug: state.districtSlug }).then((response) => {
                commit('setNutrition', response);
            });
        },
        getAllergens({ commit, state }) {
            return endpoints.getAllergens({ districtSlug: state.districtSlug }).then((response) => {
                commit('setAllergens', response);
            });
        },
        getNutritionDetails({ commit, state }) {
            return endpoints.getNutritionDetails({ siteId: state.siteID, mealTimeId: state.mealTimeId }).then((response) => {
                commit('setNutritionDetails', response);
            });
        },
        getAnnouncements({ commit, state }) {
            return endpoints.getAnnouncements({ districtSlug: state.districtSlug }).then((response) => {
                commit('setAnnouncements', response);
            });
        },
        async initializeMealTime({ dispatch, state }) {
            if (!state.mealTimeId) return;

            await dispatch('getNutritionDetails');
        },
        async initializeSite({ dispatch, commit, state }) {
            if (!state.siteID) return;

            await dispatch('getMealTimes');

            const currentMealTimeId = state.mealTimeId;
            const hasMealTime = currentMealTimeId && state.mealTimes.some((m) => m.MealTimeId === currentMealTimeId);

            if (!currentMealTimeId || !hasMealTime) {
                let defaultMealTime = state.mealTimes.find((m) => m.DisplayName === 'Lunch');
                if (!defaultMealTime && state.mealTimes.length > 0) {
                    defaultMealTime = state.mealTimes[0];
                }

                if (defaultMealTime) {
                    commit('setMealTimeId', defaultMealTime.MealTimeId);
                }
            }

            const initPromises = [dispatch('getMenuItemCategories'), dispatch('initializeMealTime')];
            await Promise.all(initPromises);
        },
        async initializeApp({ dispatch, state }, districtSlug) {
            await dispatch('setDistrictSlug', districtSlug);
            await dispatch('loadFromLocalStorage');

            const initPromises = [dispatch('getSites'), dispatch('getStyleDetails'), dispatch('getAllergens'), dispatch('getNutrition'), dispatch('getAnnouncements')];

            await Promise.all(initPromises);

            if (state.siteID) {
                await dispatch('initializeSite');
            }
        },
    },
};

export default publicMenuModule;
